class ProductVariantsModule {
  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.variantPicker = controller.query('[data-variant-picker]');
    this.variantSelect = controller.query('[data-variant-select]');
    this.variantIdInput = controller.query('[data-variant-id-input]');
    this.currentPrice = controller.query('[data-current-price]');
    this.currentPriceValue = controller.query('[data-current-price-value]') || this.currentPrice;
    this.currentPriceLabel = controller.query('[data-current-price-label]');
    this.comparePrice = controller.query('[data-compare-price]');
    this.comparePriceValue = controller.query('[data-compare-price-value]');
    this.priceBadges = controller.queryAll('[data-price-badge]');
    this.priceBadge = this.priceBadges[0] || null;
    this.soldOutBadges = controller.queryAll('[data-gallery-sold-out-badge]');
    this.priceLiveRegion = controller.query('[data-price-live]');
    this.skuDisplay = controller.query('[data-product-sku]');
    this.submitButton = controller.query('[data-submit-button]');

    if (!this.variantPicker || !this.variantSelect || this.variantSelect.options.length <= 1) return;

    this.defaultBadgeText = this.priceBadge?.textContent.trim() || '';
    this.badgeUsesPercentage = this.defaultBadgeText.includes('%');
    this.addToCartText =
      this.submitButton?.dataset.addToCartText || this.submitButton?.textContent.trim() || 'Add to Cart';
    this.soldOutText = this.submitButton?.dataset.soldOutText || 'Sold Out';
    this.optionPositions = Array.from(
      new Set(
        Array.from(this.variantPicker.querySelectorAll('[data-option-position]'))
          .map((element) => Number.parseInt(element.dataset.optionPosition, 10))
          .filter(Boolean)
      )
    ).sort((left, right) => left - right);
    this.variantOptions = Array.from(this.variantSelect.options);

    this.bindEvents();
    this.syncInitialVariant();
  }

  bindEvents() {
    this.variantPicker.addEventListener('click', (event) => {
      const card = event.target.closest('.product-variant-card[data-variant-id]');
      if (card && !card.disabled) {
        const variantOption = this.variantSelect.querySelector(`[data-variant-id="${card.dataset.variantId}"]`);
        if (variantOption) {
          this.selectVariant(variantOption);
        }
        return;
      }

      const button = event.target.closest('.product-variant-picker__button[data-option-value]');
      if (!button || button.disabled) return;

      const position = Number.parseInt(button.dataset.optionPosition, 10);
      if (!position) return;

      const options = this.getSelectedOptions();
      options[position - 1] = button.dataset.optionValue || '';
      const variantOption = this.findVariantByOptions(options);
      if (variantOption) {
        this.selectVariant(variantOption);
      }
    });

    this.variantPicker.addEventListener('change', (event) => {
      const select = event.target.closest('.product-variant-picker__select[data-option-position]');
      if (!select) return;

      const position = Number.parseInt(select.dataset.optionPosition, 10);
      if (!position) return;

      const options = this.getSelectedOptions();
      options[position - 1] = select.value;
      const variantOption = this.findVariantByOptions(options);
      if (variantOption) {
        this.selectVariant(variantOption);
      }
    });

    this.variantSelect.addEventListener('change', () => {
      const selectedOption = this.variantSelect.selectedOptions[0];
      if (selectedOption) {
        this.selectVariant(selectedOption);
      }
    });
  }

  syncInitialVariant() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    const initialVariant = variantId
      ? this.variantSelect.querySelector(`[data-variant-id="${variantId}"]`)
      : this.variantSelect.selectedOptions[0];

    if (initialVariant) {
      this.selectVariant(initialVariant, { updateUrl: false });
    }
  }

  getSelectedOptions() {
    return this.optionPositions.map((position) => {
      const activeButton = this.variantPicker.querySelector(
        `.product-variant-picker__button--active[data-option-position="${position}"]`
      );
      if (activeButton) return activeButton.dataset.optionValue || '';

      const select = this.variantPicker.querySelector(
        `.product-variant-picker__select[data-option-position="${position}"]`
      );
      return select?.value || '';
    });
  }

  findVariantByOptions(options) {
    const matchingVariants = this.findMatchingVariants(options);
    return matchingVariants.find((variantOption) => this.isVariantAvailable(variantOption)) || matchingVariants[0];
  }

  selectVariant(variantOption, { updateUrl = true } = {}) {
    if (!variantOption) return;

    this.variantSelect.value = variantOption.value;
    if (this.variantIdInput) {
      this.variantIdInput.value = variantOption.value;
    }
    this.syncControls(variantOption);
    this.updatePrice(variantOption);
    this.updateSku(variantOption);
    this.updateSubmitButton(variantOption);

    if (updateUrl && this.variantPicker.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantOption.value);
      window.history.replaceState({}, '', url.toString());
    }

    this.controller.emit('variant:change', {
      variantOption,
      variant: {
        id: variantOption.value,
        price: Number.parseInt(variantOption.dataset.variantPrice, 10) || 0,
        compareAtPrice: Number.parseInt(variantOption.dataset.variantComparePrice, 10) || 0,
        sku: variantOption.dataset.variantSku || '',
        inventoryQuantity: Number.parseInt(variantOption.dataset.variantInventory, 10) || 0,
        inventoryManagement: variantOption.dataset.variantInventoryManagement || '',
        inventoryPolicy: variantOption.dataset.variantInventoryPolicy || '',
        available: variantOption.dataset.variantAvailable === 'true',
        option1: variantOption.getAttribute('data-option-1') || '',
        option2: variantOption.getAttribute('data-option-2') || '',
        option3: variantOption.getAttribute('data-option-3') || ''
      }
    });
  }

  syncControls(variantOption) {
    this.syncVariantCards(variantOption);

    this.optionPositions.forEach((position) => {
      const selectedValue = variantOption.getAttribute(`data-option-${position}`) || '';
      if (!selectedValue) return;

      this.variantPicker
        .querySelectorAll(`.product-variant-picker__button[data-option-position="${position}"]`)
        .forEach((button) => {
          button.classList.toggle(
            'product-variant-picker__button--active',
            (button.dataset.optionValue || '').toLowerCase() === selectedValue.toLowerCase()
          );
        });

      const select = this.variantPicker.querySelector(
        `.product-variant-picker__select[data-option-position="${position}"]`
      );
      if (select) {
        select.value = selectedValue;
      }

      const labelValue = this.variantPicker.querySelector(
        `.product-variant-picker__option[data-option-position="${position}"] .product-variant-picker__label [data-option-value]`
      );
      if (labelValue) {
        labelValue.textContent = selectedValue;
      }
    });

    this.syncOptionAvailability(variantOption);
  }

  syncVariantCards(variantOption) {
    const selectedId = variantOption.value;

    this.variantPicker.querySelectorAll('.product-variant-card[data-variant-id]').forEach((card) => {
      const isActive = card.dataset.variantId === selectedId;
      card.classList.toggle('product-variant-card--active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  updatePrice(variantOption) {
    // The badges live in the gallery, so this still has work to do when the
    // price block itself is not rendered.
    if (!this.currentPriceValue && !this.priceBadges.length && !this.soldOutBadges.length) return;

    const price = Number.parseInt(variantOption.dataset.variantPrice, 10) || 0;
    const comparePrice = Number.parseInt(variantOption.dataset.variantComparePrice, 10);
    const priceFormatted = variantOption.dataset.variantPriceFormatted || this.formatMoney(price);
    const compareFormatted = variantOption.dataset.variantComparePriceFormatted || this.formatMoney(comparePrice);
    const hasComparePrice = Number.isFinite(comparePrice) && comparePrice > price;
    const available = variantOption.dataset.variantAvailable === 'true';

    if (this.currentPriceValue) {
      this.currentPriceValue.textContent = priceFormatted;
    }
    if (this.currentPriceLabel) {
      this.currentPriceLabel.textContent = `${hasComparePrice ? 'Sale price' : 'Price'}: `;
    }

    if (!hasComparePrice) {
      if (this.comparePrice) {
        this.comparePrice.style.display = 'none';
      }
      this.setBadges({ saleVisible: false, available });
      this.updatePriceAnnouncement({
        available,
        priceFormatted,
      });
      return;
    }

    if (this.comparePrice) {
      if (this.comparePriceValue) {
        this.comparePriceValue.textContent = compareFormatted;
      } else {
        this.comparePrice.textContent = compareFormatted;
      }
      this.comparePrice.style.display = 'inline';
    }

    let badgeText = this.defaultBadgeText;
    if (this.badgeUsesPercentage) {
      badgeText = `-${Math.round((1 - price / comparePrice) * 100)}%`;
    }
    this.setBadges({ saleVisible: true, available, badgeText });

    this.updatePriceAnnouncement({
      available,
      priceFormatted,
      compareFormatted,
    });
  }

  // The sale / sold-out badge sits over the gallery image, one per slide, so
  // every copy has to follow the selected variant.
  setBadges({ saleVisible, available, badgeText }) {
    this.priceBadges.forEach((badge) => {
      if (badgeText) badge.textContent = badgeText;
      badge.style.display = saleVisible && available ? '' : 'none';
    });

    this.soldOutBadges.forEach((badge) => {
      badge.style.display = available ? 'none' : '';
    });
  }

  updateSku(variantOption) {
    if (!this.skuDisplay) return;

    const sku = variantOption.dataset.variantSku || '';
    if (!sku) {
      this.skuDisplay.style.display = 'none';
      return;
    }

    this.skuDisplay.textContent = `SKU: ${sku}`;
    this.skuDisplay.style.display = 'block';
  }

  updateSubmitButton(variantOption) {
    if (!this.submitButton) return;

    const available = variantOption.dataset.variantAvailable === 'true';
    this.submitButton.disabled = !available;
    this.submitButton.textContent = available ? this.addToCartText : this.soldOutText;
  }

  isVariantAvailable(variantOption) {
    return variantOption?.dataset.variantAvailable === 'true';
  }

  getVariantOptionValue(variantOption, position) {
    return variantOption?.getAttribute(`data-option-${position}`) || '';
  }

  findMatchingVariants(options) {
    return this.variantOptions.filter((variantOption) => {
      return this.optionPositions.every((position, index) => {
        const selectedValue = options[index];
        if (!selectedValue) return true;

        const variantValue = this.getVariantOptionValue(variantOption, position);
        return variantValue.toLowerCase() === selectedValue.toLowerCase();
      });
    });
  }

  syncOptionAvailability(variantOption) {
    const selectedOptions = this.optionPositions.map((position) => this.getVariantOptionValue(variantOption, position));

    this.optionPositions.forEach((position, index) => {
      this.variantPicker
        .querySelectorAll(`.product-variant-picker__button[data-option-position="${position}"]`)
        .forEach((button) => {
          const candidateOptions = [...selectedOptions];
          const optionValue = button.dataset.optionValue || '';
          candidateOptions[index] = optionValue;

          const matchingVariants = this.findMatchingVariants(candidateOptions);
          const hasMatchingVariant = matchingVariants.length > 0;
          const isSoldOut = hasMatchingVariant && !matchingVariants.some((item) => this.isVariantAvailable(item));
          const isDisabled = !hasMatchingVariant || isSoldOut;
          const optionName = button.closest('.product-variant-picker__option')?.dataset.optionName || 'Option';
          const availabilityLabel = isSoldOut ? 'Sold out' : !hasMatchingVariant ? 'Unavailable' : '';

          button.disabled = isDisabled;
          button.classList.toggle('product-variant-picker__button--sold-out', isSoldOut);
          button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
          button.setAttribute(
            'aria-label',
            availabilityLabel ? `${optionName}: ${optionValue} (${availabilityLabel})` : `${optionName}: ${optionValue}`
          );
        });
    });
  }

  formatMoney(cents) {
    return Money.format(cents);
  }

  updatePriceAnnouncement({ available, priceFormatted, compareFormatted = '' }) {
    if (!this.priceLiveRegion) return;

    const parts = [`${available ? 'Available' : 'Sold out'}. Price: ${priceFormatted}`];
    if (compareFormatted) {
      parts[0] = `${available ? 'Available' : 'Sold out'}. Sale price: ${priceFormatted}`;
      parts.push(`Regular price: ${compareFormatted}`);
    }

    this.priceLiveRegion.textContent = parts.join('. ');
  }
}

window.HerbiProductSection.registerModule('variants', (controller) => {
  return new ProductVariantsModule(controller);
});
